import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LandingFooter from "./landing/components/LandingFooter";
import LandingHeader from "./landing/components/LandingHeader";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const SUPPORT_EMAIL = "support@synkro.ai";

const DataDeletion = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile } = useIsMobile();
  const { user } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasExistingRequest, setHasExistingRequest] = useState(false);
  const [isCheckingRequest, setIsCheckingRequest] = useState(true);
  const [deletionUserId, setDeletionUserId] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      setDeletionUserId(user.id);
    }
  }, [user]);

  const isEmailValid = useMemo(() => email.trim().length > 0 && email.includes("@"), [email]);
  const canSubmit = isEmailValid && password.trim().length >= 6;

  // Check if user already has a pending deletion request
  useEffect(() => {
    // SSR-safe: Only run API calls on client-side
    if (typeof window === 'undefined') {
      setIsCheckingRequest(false);
      return;
    }

    const checkExistingRequest = async () => {
      if (!user) {
        setIsCheckingRequest(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("deletion_requests")
          .select("id, status")
          .eq("user_id", user.id)
          .eq("status", "pending")
          .single();

        if (data) {
          setHasExistingRequest(true);
        }
        if (user?.id) {
          setDeletionUserId(user.id);
        }
        if (error && error.code !== "PGRST116") {
          // PGRST116 means no rows found, which is normal
          console.error("Error checking deletion request:", error);
        }
      } catch (err) {
        console.error("Error checking deletion request:", err);
      } finally {
        setIsCheckingRequest(false);
      }
    };

    checkExistingRequest();
  }, [user]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast({
        title: "Missing information",
        description: "Enter the email tied to your account and your current password.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError || !data.user) {
        toast({
          title: "Sign-in failed",
          description: "We couldn't verify those credentials. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const userId = data.user.id;
      setDeletionUserId(userId);

      const { data: existingRequest, error: existingRequestError } = await supabase
        .from("deletion_requests")
        .select("id, status")
        .eq("user_id", userId)
        .eq("status", "pending")
        .maybeSingle();

      if (existingRequestError && existingRequestError.code !== "PGRST116") {
        console.error("Existing deletion request check error:", existingRequestError);
        toast({
          title: "Request failed",
          description: "We couldn't verify your existing deletion request. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (existingRequest) {
        setHasExistingRequest(true);
        toast({
          title: "Deletion request already submitted",
          description: "We've already received your request and are processing it.",
        });
        return;
      }

      // Create a deletion request in the database
      const { error: requestError } = await supabase
        .from("deletion_requests")
        .insert([
          {
            user_id: userId,
            email: email.trim(),
            requested_at: new Date().toISOString(),
            status: "pending",
          },
        ]);

      if (requestError) {
        console.error("Deletion request error:", requestError);
        toast({
          title: "Request failed",
          description: "We couldn't process your deletion request. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Deletion request submitted",
        description: "We've received your request. You'll get a confirmation email within a couple of days.",
      });

      setEmail("");
      setPassword("");
      setHasExistingRequest(true);
    } catch (error) {
      console.error("Unexpected deletion error:", error);
      toast({
        title: "Unexpected error",
        description: "Please contact support@synkro.ai so we can help finish this request.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCheckingRequest) {
    return (
      <div className="landing-gradient-bg flex min-h-screen flex-col overflow-hidden transition-colors duration-300">
        <LandingHeader />
        <main className="flex-1 pt-24 pb-16 flex items-center justify-center relative z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </main>
        <LandingFooter />
      </div>
    );
  }

  const handleCancelDeletion = async () => {
    const targetUserId = user?.id ?? deletionUserId;

    if (!targetUserId) {
      toast({
        title: "Sign-in required",
        description: "Please sign back in to cancel your deletion request.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("deletion_requests")
        .delete()
        .eq("user_id", targetUserId)
        .eq("status", "pending");

      if (error) {
        console.error("Error canceling deletion request:", error);
        toast({
          title: "Failed to cancel",
          description: "We couldn't cancel your deletion request. Please try again.",
          variant: "destructive",
        });
        return;
      }

      setHasExistingRequest(false);
      toast({
        title: "Deletion request canceled",
        description: "Your deletion request has been canceled. Your account is safe.",
      });
    } catch (error) {
      console.error("Unexpected error canceling deletion:", error);
      toast({
        title: "Unexpected error",
        description: "Please contact support@synkro.ai for assistance.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hasExistingRequest) {
    return (
      <div className="landing-gradient-bg flex min-h-screen flex-col overflow-hidden transition-colors duration-300">
        <LandingHeader />
        <main className="flex-1 pt-24 pb-16 relative z-10">
          <div className={`mx-auto ${isMobile ? "px-6" : "px-8"} max-w-2xl space-y-8`}>
            <div className="text-center space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Deletion Request Already Submitted</h1>
              <p className="text-sm text-muted-foreground sm:text-base">
                We've already received your data deletion request and are processing it.
              </p>
            </div>

            <Card className="card-no-animation border-border/70">
              <CardContent className="space-y-6 p-6">
                <div className="space-y-2 text-center">
                  <p className="font-medium text-foreground">Your request is being processed</p>
                  <p className="text-sm text-muted-foreground">
                    Our privacy team will review your deletion request and send you a confirmation email within a couple of days.
                  </p>
                </div>

                <div className="flex gap-3 justify-center pt-2">
                  <Button
                    variant="outline"
                    onClick={handleCancelDeletion}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Canceling..." : "Cancel request"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="text-center text-xs text-muted-foreground space-y-1 [&_a]:underline [&_a]:text-primary [&_a]:hover:text-primary/80 dark:[&_a]:text-white dark:[&_a]:hover:text-white/80 relative z-10">
              <p>
                Questions? Email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll get back to you within 5 business days.
              </p>
              <p>
                Review our{" "}
                <Link to="/privacy">Privacy Policy</Link> to learn more about our data handling practices.
              </p>
            </div>
          </div>
        </main>
        <LandingFooter />
      </div>
    );
  }

  return (
    <div className="landing-gradient-bg flex min-h-screen flex-col overflow-hidden transition-colors duration-300">
      <LandingHeader />
      <main className="flex-1 pt-24 pb-16">
        <div className={`mx-auto ${isMobile ? "px-6" : "px-8"} max-w-2xl space-y-8`}>
          <div className="text-center space-y-3">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Data Deletion Request</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Re-enter your credentials to permanently remove your Synkro account and associated data.
            </p>
          </div>

          <Card className="card-no-animation border-border/70">
            <CardContent className="space-y-6 p-6">
              <div className="space-y-2">
                <Label htmlFor="email">Account email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  Account password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your current password"
                  required
                />
              </div>

              <Button
                type="button"
                className="w-full"
                disabled={!canSubmit || isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? "Processing..." : "Delete my account"}
              </Button>
            </CardContent>
          </Card>

          <div className="text-center text-xs text-muted-foreground space-y-1 [&_a]:underline [&_a]:text-primary [&_a]:hover:text-primary/80 dark:[&_a]:text-white dark:[&_a]:hover:text-white/80 relative z-10">
            <p>
              Need help? Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and our privacy team will follow up within 5 business days.
            </p>
            <p>
              Review our{" "}
              <Link to="/privacy">Privacy Policy</Link> for more details about how we process deletions.
            </p>
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
};

export default DataDeletion;

